/**
 * OAuth 2.0 PKCE 유틸 — 결정 #8(클라이언트 사이드 Google 신원, 서버 불필요).
 *
 * PKCE(Proof Key for Code Exchange)는 공개 클라이언트(브라우저 SPA)가 client secret
 * 없이 안전하게 authorization code 흐름을 쓰게 해준다. 흐름:
 *   1) code_verifier(고엔트로피 랜덤) 생성 → 로컬에 잠깐 보관.
 *   2) code_challenge = base64url(SHA-256(code_verifier)) 계산.
 *   3) 인증 URL에 challenge + state를 실어 사용자 동의를 받는다.
 *   4) 리다이렉트로 돌아온 code + 보관해 둔 verifier를 토큰 엔드포인트에 교환.
 *
 * 플랫폼 중립(결정 #10): Web Crypto(getRandomValues/subtle.digest)만 쓴다 —
 * 브라우저·Node 20+·RN(polyfill) 공통. 실제 네트워크 호출(토큰 교환)은 web이 한다.
 */
import { sha256Bytes } from "../util/hash.js";

function getCrypto(): Crypto {
  const c = (globalThis as { crypto?: Crypto }).crypto;
  if (!c?.getRandomValues) {
    throw new Error("Web Crypto getRandomValues를 사용할 수 없습니다. crypto 폴리필이 필요합니다.");
  }
  return c;
}

/** 바이트를 base64url(RFC 4648 §5, 패딩 제거)로 인코딩. */
export function base64UrlEncode(bytes: Uint8Array): string {
  let binary = "";
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]!);
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/**
 * PKCE code_verifier를 생성한다(RFC 7636 §4.1: 43~128자의 unreserved 문자).
 * 32바이트 랜덤 → base64url(43자)이라 규격을 만족한다.
 */
export function generateCodeVerifier(): string {
  const bytes = new Uint8Array(32);
  getCrypto().getRandomValues(bytes);
  return base64UrlEncode(bytes);
}

/** verifier로부터 S256 code_challenge를 계산한다(RFC 7636 §4.2). */
export async function deriveCodeChallenge(verifier: string): Promise<string> {
  const digest = await sha256Bytes(new TextEncoder().encode(verifier));
  return base64UrlEncode(new Uint8Array(digest));
}

/** CSRF 방어용 state(랜덤 문자열). */
export function generateState(): string {
  const bytes = new Uint8Array(16);
  getCrypto().getRandomValues(bytes);
  return base64UrlEncode(bytes);
}

/** 인증 URL을 구성하는 데 필요한 입력. */
export interface AuthUrlParams {
  /** OAuth authorization 엔드포인트(예: Google). */
  authEndpoint: string;
  clientId: string;
  redirectUri: string;
  scopes: string[];
  codeChallenge: string;
  state: string;
  /** 추가 쿼리(예: Google의 access_type=offline, prompt=consent). */
  extra?: Record<string, string>;
}

/**
 * authorization code 흐름 + PKCE(S256)용 인증 URL을 만든다. 사용자를 이 URL로
 * 보내(팝업/리다이렉트) 동의를 받는다.
 */
export function buildAuthUrl(params: AuthUrlParams): string {
  const q = new URLSearchParams({
    client_id: params.clientId,
    redirect_uri: params.redirectUri,
    response_type: "code",
    scope: params.scopes.join(" "),
    code_challenge: params.codeChallenge,
    code_challenge_method: "S256",
    state: params.state,
    ...(params.extra ?? {}),
  });
  const sep = params.authEndpoint.includes("?") ? "&" : "?";
  return `${params.authEndpoint}${sep}${q.toString()}`;
}

/** 토큰 교환(authorization code → tokens)에 보낼 폼 바디 입력. */
export interface TokenExchangeParams {
  clientId: string;
  clientSecret?: string;
  redirectUri: string;
  code: string;
  codeVerifier: string;
}

/**
 * 토큰 엔드포인트에 POST할 application/x-www-form-urlencoded 바디를 만든다.
 * Google 웹 앱 클라이언트는 PKCE + client_secret을 함께 요구한다.
 */
export function buildTokenExchangeBody(params: TokenExchangeParams): string {
  const body: Record<string, string> = {
    client_id: params.clientId,
    redirect_uri: params.redirectUri,
    code: params.code,
    code_verifier: params.codeVerifier,
    grant_type: "authorization_code",
  };
  if (params.clientSecret) body.client_secret = params.clientSecret;
  return new URLSearchParams(body).toString();
}

/** 리프레시 토큰으로 access token을 갱신할 때의 폼 바디. */
export function buildRefreshBody(clientId: string, refreshToken: string, clientSecret?: string): string {
  const body: Record<string, string> = {
    client_id: clientId,
    refresh_token: refreshToken,
    grant_type: "refresh_token",
  };
  if (clientSecret) body.client_secret = clientSecret;
  return new URLSearchParams(body).toString();
}

/** 토큰 응답(표준 OAuth). expires_in(초)은 호출부가 절대 시각으로 환산한다. */
export interface TokenResponse {
  access_token: string;
  expires_in?: number;
  refresh_token?: string;
  scope?: string;
  token_type?: string;
  /**
   * OpenID Connect id_token(JWT). `openid` 스코프 요청 시 Google이 함께 준다.
   * RoQeet Hub(결정 #31)가 Supabase GoTrue 로그인에 재사용한다.
   */
  id_token?: string;
}

/** 리다이렉트 콜백 URL(또는 그 쿼리스트링)에서 code/state/error를 파싱한다. */
export function parseAuthCallback(urlOrQuery: string): {
  code?: string;
  state?: string;
  error?: string;
} {
  let query = urlOrQuery;
  const qIdx = urlOrQuery.indexOf("?");
  if (qIdx !== -1) query = urlOrQuery.slice(qIdx + 1);
  // 해시 프래그먼트(#...)가 붙어 있으면 제거.
  const hashIdx = query.indexOf("#");
  if (hashIdx !== -1) query = query.slice(0, hashIdx);
  const params = new URLSearchParams(query);
  const out: { code?: string; state?: string; error?: string } = {};
  const code = params.get("code");
  const state = params.get("state");
  const error = params.get("error");
  if (code) out.code = code;
  if (state) out.state = state;
  if (error) out.error = error;
  return out;
}
